import { MouseEvent, useMemo, useState } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import { isSameDay, startOfDay } from 'date-fns';
import { mapI18nLocalToDateFns } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Box, Pressed, Typography } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { GlobalStyles } from 'libs/ui/styled';

import {
  DateCalendarStyled,
  PopoverContentStyled,
  PopoverStyled,
  SelectStyled,
} from './DateFilter.styles';

interface DateFilterProps {
  title: string;
  activeTimestampValues: number[];
  minDate?: Date;
  maxDate?: Date;
  disableFuture?: boolean;
  disablePast?: boolean;
  onChange: (activeTimestampValues: number[]) => void;
}

const DateFilterDay = (
  props: PickersDayProps & {
    selectedDates?: Date[];
  }
) => {
  const { day, selectedDates = [], ...other } = props;

  const selected = selectedDates.some((date) => isSameDay(date, day));

  return (
    <PickersDay
      {...other}
      disableRipple
      disableTouchRipple
      day={day}
      selected={selected}
    />
  );
};

export const DateFilter = ({
  title,
  activeTimestampValues,
  minDate,
  maxDate,
  disableFuture,
  disablePast,
  onChange,
}: DateFilterProps) => {
  const { language, t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isOpen = Boolean(anchorEl);

  const adapterLocale = useMemo(
    () => mapI18nLocalToDateFns(language),
    [language]
  );

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    const activeTimestampValuesSet = new Set(activeTimestampValues);

    const normalizedTimestamp = startOfDay(date).getTime();
    if (activeTimestampValuesSet.has(normalizedTimestamp)) {
      activeTimestampValuesSet.delete(normalizedTimestamp);
    } else {
      activeTimestampValuesSet.add(normalizedTimestamp);
    }

    onChange(Array.from(activeTimestampValuesSet));
  };

  return (
    <>
      <SelectStyled onClick={handleClick}>
        <Typography variant="captionMedium">{title}</Typography>
        {activeTimestampValues.length > 0 && (
          <Box as="span" className="dateFilter-count">
            <Typography className="dateFilter-count-label">
              {activeTimestampValues.length}
            </Typography>
          </Box>
        )}
        {isOpen ? <Icon.ArrowUp /> : <Icon.ArrowDown />}
      </SelectStyled>
      <PopoverStyled
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={isOpen}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
      >
        <PopoverContentStyled>
          <Box className="dateFilter-calendar">
            <LocalizationProvider
              adapterLocale={adapterLocale}
              dateAdapter={AdapterDateFns}
            >
              <GlobalStyles
                styles={{
                  '.MuiYearCalendar-button, .MuiMonthCalendar-button': {
                    textAlign: 'center',
                    display: 'inline',
                  },
                }}
              />
              <DateCalendarStyled
                disableFuture={disableFuture}
                disablePast={disablePast}
                maxDate={maxDate}
                minDate={minDate}
                referenceDate={
                  activeTimestampValues.length
                    ? new Date(
                        activeTimestampValues[activeTimestampValues.length - 1]
                      )
                    : undefined
                }
                slotProps={{
                  day: {
                    selectedDates: activeTimestampValues,
                  } as any,
                }}
                slots={{ day: DateFilterDay }}
                value={null}
                onChange={handleChange}
              />
            </LocalizationProvider>
          </Box>

          {activeTimestampValues.length > 0 && (
            <Box className="dateFilter-footer">
              <Pressed
                className="dateFilter-footer-clear"
                onClick={() => onChange([])}
              >
                {t('common.resetAll')}
              </Pressed>
            </Box>
          )}
        </PopoverContentStyled>
      </PopoverStyled>
    </>
  );
};
